import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, CheckCircle2, CalendarHeart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachingOptIn({ proposalType, organizationName }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/coaching/subscribe`, {
        email: email.trim(),
        name: name.trim() || null,
        proposal_type: proposalType || null,
        organization_name: organizationName || null,
      });
      setDone(true);
      toast.success(data.message || "You're on the list.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not save your subscription. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="mt-6 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-6 sm:p-7"
        data-testid="coaching-optin-success"
      >
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 shrink-0 rounded-2xl bg-emerald-100 grid place-items-center">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">
              You're on the list — see you Thursday.
            </h3>
            <p className="mt-2 text-slate-600 text-sm leading-relaxed">
              We just sent a confirmation to <b>{email}</b> with your registration
              link. We'll also send a friendly Thursday-morning nudge before the
              next session so you don't miss it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/60 p-6 sm:p-8"
      data-testid="coaching-optin-card"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="h-11 w-11 shrink-0 rounded-2xl bg-amber-100 grid place-items-center">
          <CalendarHeart className="h-6 w-6 text-amber-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900">
            Want your draft reviewed live?
          </h3>
          <p className="mt-2 text-slate-600 text-sm sm:text-base leading-relaxed">
            Drop your email and we'll send you a coaching reminder before the
            next Thursday session with Marie. No spam — just a nudge and your
            registration link.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="grid sm:grid-cols-5 gap-3">
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="sm:col-span-2 rounded-full bg-white px-5 h-12"
          disabled={busy}
          data-testid="coaching-optin-name"
        />
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="sm:col-span-2 rounded-full bg-white px-5 h-12"
          disabled={busy}
          data-testid="coaching-optin-email"
        />
        <Button
          type="submit"
          className="rounded-full bg-primary hover:bg-teal-900 h-12 px-5"
          disabled={busy}
          data-testid="coaching-optin-submit"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending
            </>
          ) : (
            <>
              <Mail className="h-4 w-4 mr-1.5" /> Send Reminder
            </>
          )}
        </Button>
      </form>

      <p className="mt-3 text-xs text-slate-500">
        You'll get an instant confirmation + a Thursday-morning nudge (max 4).
        Unsubscribe anytime.
      </p>
    </div>
  );
}
